import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Line } from 'react-chartjs-2'
import { FormControl, IconButton, Typography, TextField } from '@mui/material'
import Button from 'components/Button/Button'
import Preloader from 'components/Preloader/Preloader'
import moment from 'moment'

import botAPI from 'api/bot-api'
import { buildGraphLine } from 'utilities/number-helpers'

import s from './BotGraph.module.css'

const options = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    decimation: {
      enabled: true,
      algorithm: 'min-max',
    },
  },
}

const DEFAULT_DAYS_AGO = 1

const startTime = moment().subtract(DEFAULT_DAYS_AGO, 'days').format('YYYY-MM-DD[T]HH:mm')
const endTime = moment().format('YYYY-MM-DD[T]HH:mm')

const BotGraph = ({ botId, botLabel, handleClose }) => {
  const chartRef = useRef(null)
  const [data, setData] = useState([])
  const [key, setKey] = useState(0)
  const [period, setPeriod] = useState({ startTime, endTime })
  const [isLoading, setIsLoading] = useState(false)

  const buildBotGraph = (points) => {
    const value = buildGraphLine(points, 'value')
    const profit = buildGraphLine(points, 'profit')

    return { value, profit }
  }

  const { value, profit } = useMemo(() => buildBotGraph(data?.points || []), [data])

  const fetchData = useCallback(async () => {
    setIsLoading(true)

    const data = await botAPI.getChartData(
      botId,
      moment(period.startTime).toISOString(),
      moment(period.endTime).toISOString()
    )

    setData(data)

    setIsLoading(false)
  }, [botId, period])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId])

  useEffect(() => {
    setKey((key) => key + 1)
  }, [value])

  const handlePeriodChange = (e) => setPeriod((prev) => ({ ...prev, [e.target.id]: e.target.value }))

  if (isLoading)
    return (
      <div style={{ padding: 20 }}>
        <Preloader />
      </div>
    )

  return (
    <div className='flex-column'>
      <IconButton size='small' className={s.closeButton} onClick={handleClose} id='close_graph_icon'>
        <i className='fas fa-times' />
      </IconButton>
      <Typography variant='h4'>Bot ID: {botLabel}</Typography>
      <div className='flex-row align-center flex-wrap'>
        <FormControl variant='outlined' size='small' sx={{ m: 1, minWidth: 120 }}>
          <TextField
            id='startTime'
            label='Start time'
            type='datetime-local'
            value={period.startTime}
            onChange={handlePeriodChange}
            variant='standard'
            InputLabelProps={{
              shrink: true,
            }}
          />
        </FormControl>
        {' – '}
        <FormControl variant='outlined' size='small' sx={{ m: 1, minWidth: 120 }}>
          <TextField
            id='endTime'
            label='End time'
            type='datetime-local'
            value={period.endTime}
            onChange={handlePeriodChange}
            variant='standard'
            InputLabelProps={{
              shrink: true,
            }}
          />
        </FormControl>
        <Button onClick={fetchData}>OK</Button>
      </div>
      {!data || value?.length < 1 ? (
        <h2 style={{ textAlign: 'center' }}>No data</h2>
      ) : (
        <div style={{ position: 'relative', height: 380, width: '100%' }} id='bot_graph_chart'>
          <Line
            key={key}
            ref={chartRef}
            data={{
              datasets: [
                {
                  label: 'Value',
                  data: value,
                  pointHitRadius: 3.5,
                  pointRadius: 0,
                  borderColor: 'rgb(81, 81, 255)',
                  backgroundColor: 'rgba(81, 81, 255, .35)',
                  borderWidth: 1.5,
                },
                {
                  label: 'Profit',
                  data: profit,
                  pointHitRadius: 2.5,
                  pointRadius: 0,
                  borderColor: 'rgb(44, 255, 0)',
                  backgroundColor: 'rgba(44, 255, 0, .35)',
                  borderWidth: 2,
                },
              ],
            }}
            options={options}
            redraw={false}
          />
        </div>
      )}
    </div>
  )
}

export default BotGraph
